import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Brain,
  FileText,
  Award,
  Lightbulb,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
} from "lucide-react";
import { interviewActions } from "../../../Redux/ActionCreator.js";
import CircularProgress from "../common/CircularProgress.jsx";
import ScoreCard from "../common/ScoreCard.jsx";
import RecommendationCard from "../common/RecommendationCard.jsx";
import { ToastContainer, toast } from "react-toastify";

const AnalyticsPage = () => {
  const dispatch = useDispatch();
  const { questions, currentQuestionIndex, analytics, ui } = useSelector(
    (state) => state.interview
  );

  const { darkMode } = ui.settings;
  const { currentQuestionAnalytics } = analytics;

  const theme = {
    bg: darkMode
      ? "bg-gray-900"
      : "bg-gradient-to-br from-blue-50 to-indigo-100",
    card: darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200",
    text: darkMode ? "text-gray-100" : "text-gray-900",
    textSecondary: darkMode ? "text-gray-400" : "text-gray-600",
    button: darkMode
      ? "bg-blue-600 hover:bg-blue-700"
      : "bg-blue-600 hover:bg-blue-700",
    buttonSecondary: darkMode
      ? "bg-gray-700 hover:bg-gray-600"
      : "bg-gray-100 hover:bg-gray-200",
  };

  const [show, setShow] = useState(false);
  useEffect(() => {
    if (currentQuestionIndex == 0) {
      setShow(false);
    } else {
      setShow(true);
    }
  }, [currentQuestionIndex]);

  const handlePrevQuestion = () => {
    if (currentQuestionIndex === 0) {
      // dispatch(interviewActions.setError("This is the first question."));
      toast.error("This is the first question.", {
        position: toast.POSITION.TOP_CENTER,
      });
    } else {
      dispatch(interviewActions.setCurrentQuestion(currentQuestionIndex - 1));
      dispatch(interviewActions.setCurrentStep("interview"));
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      dispatch(interviewActions.setCurrentQuestion(currentQuestionIndex + 1));
      dispatch(interviewActions.setCurrentStep("interview"));
    } else {
      dispatch(interviewActions.calculateOverallScore());
      dispatch(interviewActions.setCurrentStep("results"));
    }
  };

  const getScoreColor = (score) => {
    if (score >= 4 && score < 6) return "from-green-500 to-green-600";
    if (score >= 2 && score < 4) return "from-orange-500 to-orange-600";
    if (score < 2) return "from-red-500 to-red-600";
    return "from-red-500 to-red-600";
  };

  const getOverallScoreStatus = (score) => {
    if (score >= 80) return { text: "Excellent", color: "text-green-600" };
    if (score >= 60) return { text: "Good", color: "text-yellow-600" };
    return { text: "Needs Improvement", color: "text-red-600" };
  };

  if (!currentQuestionAnalytics) {
    return (
      <div
        className={`min-h-screen ${theme.bg} flex items-center justify-center`}
      >
        <div
          className={`${theme.card} rounded-xl shadow-lg border p-8 text-center`}
        >
          <Brain className="h-16 w-16 text-blue-600 mx-auto mb-4" />
          <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>
            No Analytics Available
          </h2>
          <p className={`${theme.textSecondary}`}>
            Please complete the question recording and analysis first.
          </p>
        </div>
      </div>
    );
  }

  const overallScore = currentQuestionAnalytics?.AIFeedback?.overallScore || 0;
  const scoreStatus = getOverallScoreStatus(overallScore);

  return (
    <div className={`min-h-screen ${theme.bg} transition-all duration-300`}>
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className={`${theme.card} rounded-xl shadow-lg border p-6 mb-6`}>
            <div className="flex justify-between items-start">
              <div>
                <h2
                  className={`text-3xl font-bold ${theme.text} mb-2 flex items-center`}
                >
                  <Brain className="h-8 w-8 mr-3 text-blue-600" />
                  Response Analytics
                </h2>
                <p className={`${theme.textSecondary} text-lg`}>
                  Question {currentQuestionIndex + 1}:{" "}
                  {questions[currentQuestionIndex]?.question}
                </p>
              </div>
              {/* <div className="flex items-center space-x-2">
                <div
                  className={`px-4 py-2 rounded-full font-medium ${
                    overallScore >= 80
                      ? "bg-green-100 text-green-800"
                      : overallScore >= 60
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  Overall Score: {overallScore}/100
                </div>
              </div> */}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="xl:col-span-2 space-y-6">
              {/* Transcribed Response */}
              <div className={`${theme.card} rounded-xl shadow-lg border p-6`}>
                <h3
                  className={`text-xl font-semibold ${theme.text} mb-4 flex items-center`}
                >
                  <FileText className="h-5 w-5 mr-2 text-blue-600" />
                  Transcribed Response
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
                  <p
                    className={`${
                      darkMode ? "text-black" : "text-gray-900"
                    } italic text-lg leading-relaxed`}
                  >
                    "
                    {currentQuestionAnalytics?.answer ||
                      "Transcription will appear here after analysis..."}
                    "
                  </p>
                </div>
              </div>

              {/* Detailed Analytics */}
              {currentQuestionAnalytics?.analytics && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(currentQuestionAnalytics.analytics).map(
                    ([key, value]) => (
                      <ScoreCard
                        key={key}
                        title={key.replace(/([A-Z])/g, " $1").trim()}
                        score={value?.score}
                        explanation={value?.explanation}
                        theme={theme}
                        getScoreColor={getScoreColor}
                      />
                    )
                  )}
                </div>
              )}

              {/* AI Recommendations */}
              {currentQuestionAnalytics?.AIFeedback?.recommendations && (
                <div
                  className={`${theme.card} rounded-xl shadow-lg border p-6`}
                >
                  <h3
                    className={`text-xl font-semibold ${theme.text} mb-4 flex items-center`}
                  >
                    <Lightbulb className="h-5 w-5 mr-2 text-yellow-500" />
                    AI Recommendations
                  </h3>
                  <div className="space-y-4">
                    {currentQuestionAnalytics.AIFeedback.recommendations.map(
                      (rec, index) => (
                        <RecommendationCard
                          key={index}
                          recommendation={rec}
                          theme={theme}
                          darkMode={darkMode}
                        />
                      )
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Score Summary */}
              {/* <div className={`${theme.card} rounded-xl shadow-lg border p-6`}>
                <h3
                  className={`font-semibold ${theme.text} mb-4 flex items-center`}
                >
                  <Award className="h-5 w-5 mr-2 text-yellow-500" />
                  Score Summary
                </h3>
                <div className="text-center">
                  <CircularProgress
                    percentage={overallScore}
                    size={96}
                    strokeWidth={8}
                  />
                  <div
                    className={`text-2xl font-bold ${scoreStatus.color} mb-2 mt-3`}
                  >
                    {scoreStatus.text}
                  </div>
                  <p className={`text-sm ${theme.textSecondary}`}>
                    Overall Performance
                  </p>
                </div>
              </div> */}

              {/* Question Progress */}
              <div className={`${theme.card} rounded-xl shadow-lg border p-6`}>
                <h3 className={`font-semibold ${theme.text} mb-4`}>
                  Question Progress
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className={theme.textSecondary}>
                      Current Question
                    </span>
                    <span className={theme.text}>
                      {currentQuestionIndex + 1} of {questions.length}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                      style={{
                        width: `${
                          ((currentQuestionIndex + 1) / questions.length) * 100
                        }%`,
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{currentQuestionIndex + 1} completed</span>
                    <span>
                      {questions.length - currentQuestionIndex - 1} remaining
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className={`${theme.card} rounded-xl shadow-lg border p-6`}>
                <h3 className={`font-semibold ${theme.text} mb-4`}>
                  Quick Stats
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className={theme.textSecondary}>Difficulty</span>
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        questions[currentQuestionIndex]?.difficulty === "Easy"
                          ? "bg-green-100 text-green-800"
                          : questions[currentQuestionIndex]?.difficulty ===
                            "Medium"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {questions[currentQuestionIndex]?.difficulty}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme.textSecondary}>Time Limit</span>
                    <span className={theme.text}>
                      {Math.floor(
                        (questions[currentQuestionIndex]?.timeLimit || 90) / 60
                      )}
                      :
                      {((questions[currentQuestionIndex]?.timeLimit || 90) % 60)
                        .toString()
                        .padStart(2, "0")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme.textSecondary}>Category</span>
                    <span className={theme.text}>
                      {questions[currentQuestionIndex]?.category || "General"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between items-center">
            {show && (
              <button
                onClick={handlePrevQuestion}
                className={`flex items-center px-6 py-3 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200 ${theme.text}`}
              >
                <ChevronLeft className="h-5 w-5 mr-2" />
                Back to Interview
              </button>
            )}

            <button
              onClick={handleNextQuestion}
              className="ml-auto flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              {currentQuestionIndex < questions.length - 1 ? (
                <>
                  Next Question
                  <ChevronRight className="ml-2 h-5 w-5" />
                </>
              ) : (
                <>
                  Finish Interview
                  <CheckCircle className="ml-2 h-5 w-5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default AnalyticsPage;
